import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import ActionSection from '@helios/ActionSection';
import Button from '@helios/Button';
import ButtonLink from '@helios/ButtonLink';
import Card from '@helios/Card';
import Container from '@helios/Container';
import FeatureTabs from '@helios/FeatureTabs';
import Grid from '@helios/Grid';
import IconFeatureList from '@helios/IconFeatureList';
import PageHero from '@helios/PageHero';
import ProfessorCard from '@helios/ProfessorCard';
import ProgramCard from '@helios/ProgramCard';
import Section from '@helios/Section';
import StickyScrollLayout from '@helios/StickyScrollLayout';
import Layout from '@/components/Layout.js';
import useScroll from '@/hooks/useScroll.js';
import { allSites } from '@/utils/domains.js';
import heroContentStyles from '@/styles/PageHeroContent.module.scss';
import styles from '@/styles/TryCollegePage.module.scss';
import preFooterStyles from '@/styles/TryCollegePreFooter.module.scss';
import LightBadgeCheckIcon from '@/components/icons/light/badge-check';
import LightCameraSecurityIcon from '@/components/icons/light/camera-security';
import LightHandHoldingDollarIcon from '@/components/icons/light/hand-holding-dollar';
import LightScrollIcon from '@/components/icons/light/scroll';
import LightLifeRingIcon from '@/components/icons/light/life-ring';
import LightPeopleCarryBoxIcon from '@/components/icons/light/people-carry-box';
import Light1Icon from '@/components/icons/light/1';
import Light2Icon from '@/components/icons/light/2';
import Light3Icon from '@/components/icons/light/3';
import Light4Icon from '@/components/icons/light/4';
import Light5Icon from '@/components/icons/light/5';
import ThinArrowDownLongIcon from '@/components/icons/thin/arrow-down-long';
import PageBanner from '@/public/images/b2c-landing.jpg';
import JosePhine from '@/public/images/josephine-thumbnail.png';
import IntroToBusiness from '@/public/images/intro-to-business.png';
import Composition from '@/public/images/composition.png';
import IntroToMarketing from '@/public/images/intro-to-marketing.png';
// TODO: replace with a dedicated microeconomics.png asset once available
import MicroeconomicsImage from '@/public/images/macroeconomics.png';
import ToddFitch from '@/public/images/professors/todd-fitch_cover.jpg';
import TheresaWalsh from '@/public/images/professors/theresa-walsh_cover.jpg';
import DanGoldsmith from '@/public/images/professors/dan-goldsmith_cover.jpg';
import DenizBaglan from '@/public/images/professors/deniz-baglan_cover.jpg';
import PlatFormSlug1 from '@/public/images/platform-slug-1.jpg';
import PlatFormSlug2 from '@/public/images/platform-slug-2.jpg';
import PlatFormSlug3 from '@/public/images/platform-slug-3.jpg';
import PlatFormSlug5 from '@/public/images/platform-slug-7.jpg';
import faqVector from '@/public/images/faqvector.svg';
const APPLY_HREF = 'https://campusteaching.typeform.com/to/RJxORffM';

// The rendered widths (Phase 5 PR 5.4): the step column is the page width
// less the 20px gutters below 992px, the page less the 500px sidebar and the
// gutters from 992px, and at most 840px.
// Professors: two columns, four from 768px, three from 992px, four from
// 1200px, 30px apart.
const PROFESSOR_IMAGE_SIZES = '(max-width: 767px) calc((100vw - 70px) / 2), (max-width: 991px) calc((100vw - 130px) / 4), (max-width: 1199px) calc((100vw - 600px) / 3), 188px';
// The open experience item's image: the step column below 992px, the
// FeatureTabs panel (840 − 420 − 60) from 992px.
const EXPERIENCE_IMAGE_SIZES = '(max-width: 991px) calc(100vw - 40px), 360px';
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}
function ExperienceImage(t0) {
  const $ = _c(2);
  const {
    src
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = <Image src={src} alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} />;
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ProfessorPhoto(src, alt) {
  return {
    src,
    alt,
    sizes: PROFESSOR_IMAGE_SIZES
  };
}
function TryCollege(t0) {
  const $ = _c(85);
  const {
    site
  } = t0;
  const {
    scrollTo
  } = useScroll();
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={styles["overview-icon"]}><LightBadgeCheckIcon /></div>;
    t2 = <h4 className={styles["overview-eyebrow"]}>about campus</h4>;
    t3 = <h3>Campus is an accredited college on a mission to maximize access to world-class education.</h3>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      id: "overview",
      label: "Overview",
      content: <div className={styles.section}><Card surface="subtle" className={styles["overview-card"]}>{t1}{t2}{t3}<p>We're making our online courses available, for college credit, for high school students and recent graduates at{" "}<strong>no tuition cost</strong> as part of our Try College program.</p></Card></div>
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h3 className={styles["step-title"]}>Why enroll in the Campus Try College program?</h3>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className={styles["university-mark"]} />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      icon: t6,
      title: "Renowned professors",
      body: <p>Learn from empathetic and enthusiastic instructors with experience teaching at top colleges and universities</p>
    };
    t8 = <LightCameraSecurityIcon />;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  let t10;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      icon: t8,
      title: "Live classes",
      body: <p>Receive personal attention from professors and TAs during live classes and office hours</p>
    };
    t10 = <LightHandHoldingDollarIcon />;
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  let t11;
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      icon: t10,
      title: "Free college course",
      body: <p>Students enrolling in our program will be able to take a college course at absolutely no cost</p>
    };
    t12 = <LightScrollIcon />;
    $[10] = t11;
    $[11] = t12;
  } else {
    t11 = $[10];
    t12 = $[11];
  }
  let t13;
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      icon: t12,
      title: "Earn college credit",
      body: <p>Reduce the cost of tuition and the time to graduate by earning college credit as a high school student or recent graduate</p>
    };
    t14 = <LightLifeRingIcon />;
    $[12] = t13;
    $[13] = t14;
  } else {
    t13 = $[12];
    t14 = $[13];
  }
  let t15;
  let t16;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      icon: t14,
      title: "Unlimited math and writing tutoring",
      body: <p>Students have 24/7 access to tutoring in these areas at no additional cost</p>
    };
    t16 = <LightPeopleCarryBoxIcon />;
    $[14] = t15;
    $[15] = t16;
  } else {
    t15 = $[14];
    t16 = $[15];
  }
  let t17;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className={styles.features}><IconFeatureList columns={3} titleAs="h4" items={[t7, t9, t11, t13, t15, {
        icon: t16,
        title: "Personal success coach",
        body: <p>Communicate with a personal success coach who will serve as an advisor and confidant</p>
      }]} /></div>;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ActionSection layout="split" surface="panel" title="Ready to apply? Get started." titleScale="compact" actions={<ButtonLink href={APPLY_HREF} target="_blank" rel="noreferrer" arrow={true}>Apply now</ButtonLink>}><p>Get a head start with our Try College program.</p></ActionSection>;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  let t19;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p><strong>“I’ve never had this many great professors all at one time.</strong>{" "}They're all eager to teach us. They all love their jobs, they all love what they teach, and they all want us to succeed.”</p>;
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className={styles.avatar}><Image src={JosePhine} alt="Jose Phine" sizes="60px" /></div>;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  let t21;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      id: "why-campus",
      label: "Why Campus?",
      content: <div className={styles.section}>{t5}{t17}{t18}<div className={styles.testimonial}><div className={styles["fancy-card"]}>{t19}<div className={styles.person}>{t20}<div className={styles["person-info"]}><h5 className={styles["person-name"]}><span>JOSEPHINE L.</span> ALUMNA</h5><p className={styles["person-detail"]}>A.A. Business Administration</p></div></div></div></div></div>
    };
    $[20] = t21;
  } else {
    t21 = $[20];
  }
  let t22;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <h3 className={styles["step-title"]}>Courses</h3>;
    $[21] = t22;
  } else {
    t22 = $[21];
  }
  let t23;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      base: 1,
      sm: 2,
      md: 3,
      lg: 2,
      xl: 3
    };
    $[22] = t23;
  } else {
    t23 = $[22];
  }
  let t24;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      id: "course-preview",
      label: "Course preview",
      content: <div className={styles.section}>{t22}<Grid columns={t23} gap="sm"><ProgramCard image={IntroToBusiness} imageAlt="Introduction to Business" title="Introduction to Business" description="Delve into various forms of business ownership, functions, and roles." duration="11 weeks" schedule="Meets twice per week" /><ProgramCard image={IntroToMarketing} imageAlt="Principles of Marketing" title="Principles of Marketing" description="Learn the basics of marketing and current theory on consumer decision-making." duration="11 weeks" schedule="Meets twice per week" /><ProgramCard image={MicroeconomicsImage} imageAlt="Principles of Microeconomics" title="Principles of Microeconomics" description="Understand the role of economics in business and in public and private decision-making." duration="11 weeks" schedule="Meets twice per week" /><ProgramCard image={Composition} imageAlt="English Composition" title="English Composition" description="Develop and organize expository prose with short and long composition writing." duration="11 weeks" schedule="Meets twice per week" /></Grid></div>
    };
    $[23] = t24;
  } else {
    t24 = $[23];
  }
  let t25;
  let t26;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <h3 className={styles["step-title"]}>Professors</h3>;
    t26 = <p><ButtonLink href="/professors" variant="text" tone="neutral" className={styles["professors-link"]} arrow={true}>Learn more about our professors</ButtonLink></p>;
    $[24] = t25;
    $[25] = t26;
  } else {
    t25 = $[24];
    t26 = $[25];
  }
  let t27;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      base: 2,
      md: 4,
      lg: 3,
      xl: 4
    };
    $[26] = t27;
  } else {
    t27 = $[26];
  }
  let t28;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <ProfessorCard affiliation="UC Berkeley" image={ProfessorPhoto(ToddFitch, "Todd Fitch")} layout="stacked" name="Todd Fitch" nameAs="h6" />;
    $[27] = t28;
  } else {
    t28 = $[27];
  }
  let t29;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ProfessorCard affiliation="UC Davis" image={ProfessorPhoto(TheresaWalsh, "Theresa Walsh")} layout="stacked" name="Theresa Walsh" nameAs="h6" />;
    $[28] = t29;
  } else {
    t29 = $[28];
  }
  let t30;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ProfessorCard affiliation="UMD" image={ProfessorPhoto(DanGoldsmith, "Dan Goldsmith")} layout="stacked" name="Dan Goldsmith" nameAs="h6" />;
    $[29] = t30;
  } else {
    t30 = $[29];
  }
  let t31;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      id: "professors",
      label: "Professors",
      content: <div className={styles.section}>{t25}{t26}<Grid columns={t27} gap="md">{t28}{t29}{t30}<ProfessorCard affiliation="Howard" image={ProfessorPhoto(DenizBaglan, "DenizBaglan")} layout="stacked" name="Deniz Baglan" nameAs="h6" /></Grid></div>
    };
    $[30] = t31;
  } else {
    t31 = $[30];
  }
  let t32;
  let t33;
  let t34;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <h3 className={styles["step-title"]}>Application process</h3>;
    t33 = <h4 className={styles["step-subtitle"]}>Requirements to apply</h4>;
    t34 = <p>To be eligible for admissions, students must:</p>;
    $[31] = t32;
    $[32] = t33;
    $[33] = t34;
  } else {
    t32 = $[31];
    t33 = $[32];
    t34 = $[33];
  }
  let t35;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <ul className={styles.list}><li>Be in their third or fourth year of high school and be at least age 15 by the start of classes, OR be a recent high school graduate (having graduated no earlier than June 2023)</li><li>Have a minimum 2.5 cumulative GPA</li><li>Be able to commit a minimum of 7-10 hours per week (including class time)</li></ul>;
    $[34] = t35;
  } else {
    t35 = $[34];
  }
  let t36;
  let t37;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className={styles["step-icon"]}><Light1Icon /></div>;
    t37 = <h4 className={styles["step-name"]}>Apply</h4>;
    $[35] = t36;
    $[36] = t37;
  } else {
    t36 = $[35];
    t37 = $[36];
  }
  let t38;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className={styles.step}>{t36}{t37}<p>Complete the online{" "}<a className={styles["application-link"]} href={APPLY_HREF} target="_blank" rel="noreferrer"><strong>application</strong></a>{"."} No fee or standardized test scores required.</p></div>;
    $[37] = t38;
  } else {
    t38 = $[37];
  }
  let t39;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className={styles.step}><div className={styles["step-icon"]}><Light2Icon /></div><h4 className={styles["step-name"]}>Attend an info session</h4><p>Once you apply and are admitted, you'll be invited to an info session that gives you a rundown of the program.</p><p>If you plan to enroll, attending an info session is mandatory.</p></div>;
    $[38] = t39;
  } else {
    t39 = $[38];
  }
  let t40;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className={styles.step}><div className={styles["step-icon"]}><Light3Icon /></div><h4 className={styles["step-name"]}>Sign enrollment documents</h4><p>Students must sign the program enrollment agreement, which will be provided via email and can be signed with an electronic signature. A parent or guardian signature is required for students under 18.</p></div>;
    $[39] = t40;
  } else {
    t40 = $[39];
  }
  let t41;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className={styles.step}><div className={styles["step-icon"]}><Light4Icon /></div><h4 className={styles["step-name"]}>Register for courses</h4><p>Students can then reserve a spot in an upcoming available course.</p></div>;
    $[40] = t41;
  } else {
    t41 = $[40];
  }
  let t42;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className={styles.step}><div className={styles["step-icon"]}><Light5Icon /></div><h4 className={styles["step-name"]}>Attend orientation</h4><p>Students will get an introduction to the program, including how to navigate the student platform and opportunities to meet program staff and fellow classmates.</p></div>;
    $[41] = t42;
  } else {
    t42 = $[41];
  }
  let t43;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <h4>Key dates and deadlines</h4>;
    $[42] = t43;
  } else {
    t43 = $[42];
  }
  let t44;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      base: 1,
      md: 2
    };
    $[43] = t44;
  } else {
    t44 = $[43];
  }
  let t45;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <p className={styles["dates-header"]}>Before classes</p>;
    $[44] = t45;
  } else {
    t45 = $[44];
  }
  let t46;
  let t47;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <li>Application deadline: September 17, 2024</li>;
    t47 = <li>Enrollment deadline: September 24, 2024</li>;
    $[45] = t46;
    $[46] = t47;
  } else {
    t46 = $[45];
    t47 = $[46];
  }
  let t48;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <div className={styles.dates}>{t45}<ul className={styles["dates-list"]}>{t46}{t47}<li>Student orientation:<br />Week of September 30, 2024</li></ul></div>;
    $[47] = t48;
  } else {
    t48 = $[47];
  }
  let t49;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <p className={styles["dates-header"]}>First day of term</p>;
    $[48] = t49;
  } else {
    t49 = $[48];
  }
  let t50;
  let t51;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <li>Classes start: October 7, 2024</li>;
    t51 = <li>Add/drop deadline: October 18, 2024</li>;
    $[49] = t50;
    $[50] = t51;
  } else {
    t50 = $[49];
    t51 = $[50];
  }
  let t52;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <Card surface="subtle" className={styles["deadline-card"]}>{t43}<Grid columns={t44} gap="md">{t48}<div className={styles.dates}>{t49}<ul className={styles["dates-list"]}>{t50}{t51}<li>Final exam week:<br />December 16, 2024 - December 19, 2024</li></ul></div></Grid></Card>;
    $[51] = t52;
  } else {
    t52 = $[51];
  }
  let t53;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      id: "admissions",
      label: "Admissions",
      content: <div className={styles.section}>{t32}{t33}{t34}{t35}{t38}{t39}{t40}{t41}{t42}{t52}<ActionSection layout="split" surface="panel" title="Interested in the program?" titleScale="compact" actions={<ButtonLink href={APPLY_HREF} target="_blank" rel="noreferrer" arrow={true}>Apply now</ButtonLink>}><p>Start an application for our Try College program.</p></ActionSection></div>
    };
    $[52] = t53;
  } else {
    t53 = $[52];
  }
  let t54;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      id: "tuition",
      label: "Tuition",
      content: <div className={styles.section}><h3 className={styles["step-title"]}>Tuition</h3><p>No tuition will be charged for students enrolled in any course under the Try College program.</p><h4 className={styles["step-subtitle"]}>Course materials</h4><p>All textbooks and other required course materials are free to access and readily available online.</p></div>
    };
    $[53] = t54;
  } else {
    t54 = $[53];
  }
  let t55;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <h3 className={styles["step-title"]}>Student experience</h3>;
    $[54] = t55;
  } else {
    t55 = $[54];
  }
  let t56;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = {
      id: "live-classes",
      title: "Live classes",
      body: <p>All classes in the Try College program are live so that you can directly interact with professors and peers. We keep things engaging by holding lively discussions and debates, breakout sessions, forums, and more.</p>,
      media: <ExperienceImage src={PlatFormSlug1} />
    };
    $[55] = t56;
  } else {
    t56 = $[55];
  }
  let t57;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = {
      id: "tutoring",
      title: "Tutoring",
      body: <p>We provide students with unlimited access to 1 on 1 math and writing tutoring at no additional cost. Our tutoring services are accessible 24/7 so that you can get the academic support you need, whenever and wherever.</p>,
      media: <ExperienceImage src={PlatFormSlug2} />
    };
    $[56] = t57;
  } else {
    t57 = $[56];
  }
  let t58;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = {
      id: "office-hours",
      title: "Office hours",
      body: <p>Benefit from unprecedented access to professors from world-class universities and accomplished TAs through live 1:1 office hours. Faculty are available every week so you can make connections, build rapport, and deepen your knowledge.</p>,
      media: <ExperienceImage src={PlatFormSlug3} />
    };
    $[57] = t58;
  } else {
    t58 = $[57];
  }
  let t59;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = {
      id: "experience",
      label: "Experience",
      content: <div className={styles.section}>{t55}<FeatureTabs label="Student experience" items={[t56, t57, t58, {
          id: "events",
          title: "Events",
          body: <p>Explore your career options and learn more about the journey to get there through exclusive Campus-hosted events featuring industry experts from influential companies like Google, TikTok, and Spotify. Also enjoy game nights, trivia nights, and themed social events.</p>,
          media: <ExperienceImage src={PlatFormSlug5} />
        }]} /></div>
    };
    $[58] = t59;
  } else {
    t59 = $[58];
  }
  let t60;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = classNames(styles.section, styles.faq);
    $[59] = t60;
  } else {
    t60 = $[59];
  }
  let t61;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <p className={styles["faq-title"]}>Questions people often ask us:</p>;
    $[60] = t61;
  } else {
    t61 = $[60];
  }
  let t62;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <div className={styles["faq-header"]}>{t61}<div className={styles["faq-image"]}><Image src={faqVector} alt="Image" sizes="420px" /></div></div>;
    $[61] = t62;
  } else {
    t62 = $[61];
  }
  let t63;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#about-campus">About Campus</Link></div>;
    $[62] = t63;
  } else {
    t63 = $[62];
  }
  let t64;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#courses-and-professors">Courses and professors</Link></div>;
    $[63] = t64;
  } else {
    t64 = $[63];
  }
  let t65;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#student-experience-and-academics">Student experience and academics</Link></div>;
    $[64] = t65;
  } else {
    t65 = $[64];
  }
  let t66;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#high-school-and-transfer-credit">High school and transfer credit</Link></div>;
    $[65] = t66;
  } else {
    t66 = $[65];
  }
  let t67;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#tuition">Tuition</Link></div>;
    $[66] = t67;
  } else {
    t67 = $[66];
  }
  let t68;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#key-dates">Key dates</Link></div>;
    $[67] = t68;
  } else {
    t68 = $[67];
  }
  let t69;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = [t4, t21, t24, t31, t53, t54, t59, {
      id: "faq",
      label: "FAQ",
      content: <div className={t60}>{t62}<nav className={styles["faq-nav"]} aria-label="Try College FAQ sections">{t63}{t64}{t65}{t66}{t67}{t68}<div className={styles["faq-nav-item"]}><Link className={styles["faq-nav-link"]} href="/programs/try-college-faq#for-parents">For parents</Link></div></nav></div>
    }];
    $[68] = t69;
  } else {
    t69 = $[68];
  }
  const steps = t69;
  let t70;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <Head><title>Campus | Try College | Free College Course</title><link rel="canonical" href="https://campus.edu/programs/try-college" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="The Campus Try College program offers high school juniors, seniors, and recent graduates the opportunity to take a college course at no tuition cost. Learn more." /><meta name="keywords" content="Free college course" /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[69] = t70;
  } else {
    t70 = $[69];
  }
  let t71;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <div className={styles.banner}><Image className={styles["banner-image"]} priority={true} src={PageBanner} alt="Try College Program" sizes="100vw" /></div>;
    $[70] = t71;
  } else {
    t71 = $[70];
  }
  let t72;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = classNames(heroContentStyles.title, heroContentStyles["title--feature"]);
    $[71] = t72;
  } else {
    t72 = $[71];
  }
  let t73;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <span>no tuition cost</span>;
    $[72] = t73;
  } else {
    t73 = $[72];
  }
  let t74;
  let t75;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <h1 className={t72}>Take a college course at Campus at{" "}{t73} in our <span>Try College</span>{" "}program.</h1>;
    t75 = <ButtonLink href={APPLY_HREF} target="_blank" rel="noreferrer" arrow={true}>Apply now</ButtonLink>;
    $[73] = t74;
    $[74] = t75;
  } else {
    t74 = $[73];
    t75 = $[74];
  }
  let t76;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = <ThinArrowDownLongIcon />;
    $[75] = t76;
  } else {
    t76 = $[75];
  }
  let t77;
  if ($[76] !== scrollTo) {
    t77 = <div className={styles.hero}><PageHero>{t71}<PageHero.Container><PageHero.Content><div className={styles["hero-copy"]}>{t74}{t75}<Button className={styles["scroll-button"]} variant="text" tone="neutral" size="sm" iconRight={t76} onClick={() => scrollTo(document.querySelector("[data-scroll-step=\"1\"]"))}>Learn more</Button></div></PageHero.Content></PageHero.Container></PageHero></div>;
    $[76] = scrollTo;
    $[77] = t77;
  } else {
    t77 = $[77];
  }
  let t78;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <StickyScrollLayout container="wide" steps={steps} variant="wide" />;
    $[78] = t78;
  } else {
    t78 = $[78];
  }
  let t79;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <Section><Container><div className={preFooterStyles.root}><h2 className={preFooterStyles.heading}>Classes start October 7th</h2><h3 className={preFooterStyles.subheading}>Applications due September 17, 2024</h3><p className={preFooterStyles.body}>If you're savvy, ambitious, and looking to get a head-start on college while learning from some of the best professors in the country, we should talk.</p><ButtonLink href={APPLY_HREF} target="_blank" rel="noreferrer" arrow={true}>Apply now</ButtonLink></div></Container></Section>;
    $[79] = t79;
  } else {
    t79 = $[79];
  }
  let t80;
  if ($[80] !== t77) {
    t80 = <div id="wrapper">{t70}{t77}{t78}{t79}</div>;
    $[80] = t77;
    $[81] = t80;
  } else {
    t80 = $[81];
  }
  let t81;
  if ($[82] !== site || $[83] !== t80) {
    t81 = <Layout site={site} marqueeDisabled={true}>{t80}</Layout>;
    $[82] = site;
    $[83] = t80;
    $[84] = t81;
  } else {
    t81 = $[84];
  }
  return t81;
}
export default TryCollege;